import {
  addHours,
  format,
  isSameDay,
  parseISO,
  subSeconds,
  isValid,
  parse,
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

import {
  CalendarCategory,
  CalendarEvent,
} from './Calendar';
import { METADATA_MAP } from '../../constants/nylas';

export function getUserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

const parseDateString = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

function getCalendarCategoryColor(event: CalendarEvent, calendarCategories: CalendarCategory[]) {
  const categoryId = event.calendarCategoryId;
  const category = calendarCategories.find((category) => +category.id === +categoryId);
  return category?.color || 'salmon';
}

export function convertUserTimeToEventTimezone(
  dateTime: Date,
  fromTimezone: string,
  toTimezone: string
): Date {
  console.log('=== convertUserTimeToEventTimezone START ===');
  console.log('Input:', { dateTime, fromTimezone, toTimezone });
  
  if (!fromTimezone || !toTimezone || fromTimezone === toTimezone || 
      fromTimezone.trim() === '' || toTimezone.trim() === '') {
    console.log('No conversion needed, returning original date');
    console.log('=== convertUserTimeToEventTimezone END ===');
    return dateTime;
  }
  
  try {
    const utcTime = zonedTimeToUtc(dateTime, fromTimezone);
    const result = utcToZonedTime(utcTime, toTimezone);
    
    console.log('Conversion result:', {
      original: dateTime.toString(),
      utc: utcTime.toString(),
      converted: result.toString()
    });
    console.log('=== convertUserTimeToEventTimezone END ===');
    
    return result;
  } catch (error) {
    console.error('Error converting timezone:', error);
    console.log('=== convertUserTimeToEventTimezone END (error) ===');
    return dateTime;
  }
}

export function filterByCalendarCategories(parsedEventList, selectedCalendarCategories) {
  console.log('=== filterByCalendarCategories START ===');
  console.log('Input events:', parsedEventList?.length);
  console.log('Selected categories:', selectedCalendarCategories);
  
  if (!parsedEventList || !selectedCalendarCategories) {
    console.log('No events or categories, returning empty array');
    console.log('=== filterByCalendarCategories END ===');
    return [];
  }
  
  // Ensure selectedCalendarCategories are numbers
  const selectedIds = selectedCalendarCategories.map(id => parseInt(id, 10));
  
  const filtered = parsedEventList.filter((event) => {
    const eventCategoryId = parseInt(event.calendarCategoryId, 10);
    return selectedIds.includes(eventCategoryId);
  });
  
  console.log('Filtered events:', filtered.length);
  console.log('=== filterByCalendarCategories END ===');
  
  return filtered;
}

export function eventWithCategories(
  events: CalendarEvent[], 
  calendarCategories: CalendarCategory[]
): CalendarEvent[] {
  console.log('=== eventWithCategories START ===');
  console.log('Input events:', events?.map(e => ({
    id: e?.id,
    title: e?.title,
    allDay: e?.allDay,
    start: e?.start,
    end: e?.end,
    metadata: e?.metadata
  })));
  
  if (!events?.length) {
    console.log('No events, returning empty array');
    return [];
  }
  
  const userTimezone = getUserTimeZone();
  
  const result = events.map((event) => {
    if (!event) return null;
    
    // Extract calendarCategoryId from metadata
    let calendarCategoryId = event.calendarCategoryId;
    if (!calendarCategoryId && event.metadata) {
      calendarCategoryId = event.metadata[METADATA_MAP.calendar_category_id] || event.metadata.key1;
      if (calendarCategoryId && typeof calendarCategoryId === 'string') {
        calendarCategoryId = parseInt(calendarCategoryId, 10);
      }
    }
    
    // FIX: Proper date parsing for all-day events
    const parseDate = (date: any, isAllDay: boolean): Date => {
      if (!date) {
        console.log('Date is null/undefined, returning new Date()');
        return new Date();
      }
      
      console.log(`Parsing ${isAllDay ? 'all-day' : 'timed'} date:`, date, 'type:', typeof date);
      
      if (date instanceof Date) {
        if (isAllDay) {
          // For all-day events, create date without time component
          const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
          console.log('Created date-only from Date object:', dateOnly.toString());
          return dateOnly;
        }
        return date;
      }
      
      if (typeof date === 'string') {
        if (isAllDay) {
          // FIX: Handle all-day dates properly - parse as LOCAL date, not UTC
          // Check if string is in YYYY-MM-DD format
          const dateMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (dateMatch) {
            const [, year, month, day] = dateMatch;
            // Create date in LOCAL timezone (this is the key fix)
            const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            console.log('Parsed YYYY-MM-DD as local date:', localDate.toString());
            return localDate;
          }
          
          // Check if it's ISO string (e.g., "2025-12-16T00:00:00Z")
          const isoMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})T/);
          if (isoMatch) {
            const [, year, month, day] = isoMatch;
            // Create date in LOCAL timezone from ISO components
            const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            console.log('Extracted date from ISO string as local date:', localDate.toString());
            return localDate;
          }
          
          // Try to parse as date
          const dateObj = new Date(date);
          if (!isNaN(dateObj.getTime())) {
            const localDate = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
            console.log('Parsed generic string as local date:', localDate.toString());
            return localDate;
          }
        } else {
          // For timed events, parse normally
          const dateObj = new Date(date);
          return isNaN(dateObj.getTime()) ? new Date() : dateObj;
        }
      }
      
      // Fallback
      console.log('Using fallback parsing');
      const dateObj = new Date(date);
      return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
    };
    
    const isAllDay = event.allDay;
    const startDate = parseDate(event.start, isAllDay);
    let endDate = parseDate(event.end, isAllDay);

    // FIX: For all-day events, Nylas/iCalendar uses exclusive end dates
    // (event on Jan 20 has end: Jan 21). Subtract 1 day to get inclusive end date.
    if (isAllDay && endDate > startDate) {
      const adjustedEnd = new Date(endDate);
      adjustedEnd.setDate(adjustedEnd.getDate() - 1);
      endDate = adjustedEnd;
      console.log('Adjusted all-day end date (subtracted 1 day):', endDate.toString());
    }

    console.log('Parsed dates for event:', {
      id: event.id,
      isAllDay,
      originalStart: event.start,
      originalEnd: event.end,
      parsedStart: startDate.toString(),
      parsedEnd: endDate.toString(),
      dateNumbers: {
        startDate: startDate.getDate(),
        startMonth: startDate.getMonth() + 1,
        startYear: startDate.getFullYear(),
        endDate: endDate.getDate(),
        endMonth: endDate.getMonth() + 1,
        endYear: endDate.getFullYear()
      }
    });
    
    const originalTimezone = event.metadata?.[METADATA_MAP.original_timezone] || 
                            event.originalTimezone || 
                            event.timezone || 
                            userTimezone;
    
    const timezone = event.timezone || originalTimezone;
    
    const processedEvent = {
      ...event,
      calendarCategoryId: calendarCategoryId || event.calendarCategoryId || 0,
      color: getCalendarCategoryColor(
        {...event, calendarCategoryId: calendarCategoryId || event.calendarCategoryId}, 
        calendarCategories
      ),
      timezone: timezone,
      originalTimezone: originalTimezone,
      start: startDate,
      end: endDate,
      displayTimezone: userTimezone
    };
    
    return processedEvent;
  }).filter(Boolean);
  
  console.log('Result events:', result.map(e => ({
    id: e?.id,
    title: e?.title,
    allDay: e?.allDay,
    start: e?.start?.toString(),
    end: e?.end?.toString(),
    startDateNum: e?.start?.getDate(),
    startMonthNum: e?.start?.getMonth() + 1
  })));
  console.log('=== eventWithCategories END ===');
  
  return result;
}

export function convertEventTimeToUserTimezone(
  event: CalendarEvent, 
  userTimezone: string
): CalendarEvent {
  console.log('=== convertEventTimeToUserTimezone START ===');
  console.log('Event:', {
    id: event.id,
    title: event.title,
    allDay: event.allDay,
    start: event.start,
    end: event.end,
    startISO: event.start?.toISOString?.(),
    endISO: event.end?.toISOString?.(),
    timezone: event.timezone,
    originalTimezone: event.originalTimezone
  });
  console.log('User timezone:', userTimezone);
  
  // FIX: For all-day events, dates should already be correct from eventWithCategories
  // No timezone conversion needed for all-day events
  if (event.allDay) {
    console.log('Processing as ALL-DAY event - returning as-is');
    
    const result = {
      ...event,
      displayTimezone: userTimezone,
      originalTimezone: event.originalTimezone || event.timezone || userTimezone,
      timezone: event.timezone || userTimezone,
    };
    
    console.log('ALL-DAY Result:', {
      start: result.start.toString(),
      end: result.end.toString(),
      startDate: result.start?.getDate?.(),
      startMonth: result.start?.getMonth?.(),
      startYear: result.start?.getFullYear?.(),
    });
    console.log('=== convertEventTimeToUserTimezone END ===');
    
    return result;
  }
   
  console.log('Processing as TIMED event');
  
  if (!event.timezone || event.timezone.trim() === '') {
    const startDate = event.start instanceof Date ? event.start : new Date(event.start);
    const endDate = event.end instanceof Date ? event.end : new Date(event.end);
    
    const result = {
      ...event,
      start: startDate,
      end: endDate,
      displayTimezone: userTimezone,
      originalTimezone: userTimezone,
    };
    
    console.log('TIMED Result (no tz):', {
      start: result.start.toString(),
      end: result.end.toString()
    });
    console.log('=== convertEventTimeToUserTimezone END ===');
    
    return result;
  }
  
  if (event.timezone === userTimezone) {
    const startDate = event.start instanceof Date ? event.start : new Date(event.start);
    const endDate = event.end instanceof Date ? event.end : new Date(event.end);
    
    const result = {
      ...event,
      start: startDate,
      end: endDate,
      displayTimezone: userTimezone,
      originalTimezone: event.originalTimezone || event.timezone,
    };
    
    console.log('TIMED Result (same tz):', {
      start: result.start.toString(),
      end: result.end.toString()
    });
    console.log('=== convertEventTimeToUserTimezone END ===');
    
    return result;
  }
  
  try {
    const startDate = typeof event.start === 'string' ? new Date(event.start) : event.start;
    const endDate = typeof event.end === 'string' ? new Date(event.end) : event.end;
    
    const startUtc = zonedTimeToUtc(startDate, event.displayTimezone);
    const endUtc = zonedTimeToUtc(endDate, event.displayTimezone);
    
    const startInUserTz = utcToZonedTime(startUtc, userTimezone);
    const endInUserTz = utcToZonedTime(endUtc, userTimezone);
    
    const result = {
      ...event,
      start: startInUserTz,
      end: endInUserTz,
      displayTimezone: userTimezone,
      originalTimezone: event.originalTimezone || event.timezone,
    };
    
    console.log('TIMED Result (converted):', {
      start: result.start.toString(),
      end: result.end.toString()
    });
    console.log('=== convertEventTimeToUserTimezone END ===');
    
    return result;
  } catch (error) {
    console.error('Error converting timezone:', error);
    
    const startDate = event.start instanceof Date ? event.start : new Date(event.start);
    const endDate = event.end instanceof Date ? event.end : new Date(event.end);
    
    const result = {
      ...event,
      start: startDate,
      end: endDate,
      displayTimezone: userTimezone,
      originalTimezone: event.originalTimezone || event.timezone,
    };
    
    return result;
  }
}

export function formatDates({ start, end, allDay }) {
  console.log('=== formatDates START ===');
  console.log('Input:', { start, end, allDay });
  
  if (allDay) {
    const parseAllDayDate = (date: any): Date | null => {
      if (!date) return null;
      
      if (date instanceof Date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
      }
      
      if (typeof date === 'string') {
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
        }
      }
      
      return null;
    };
    
    const parsedStart = parseAllDayDate(start);
    const parsedEnd = parseAllDayDate(end) || parsedStart;
    
    console.log('ALL-DAY Result:', {
      parsedStart: parsedStart?.toString(),
      parsedEnd: parsedEnd?.toString()
    });
    console.log('=== formatDates END ===');
    
    return {
      parsedStart: parsedStart,
      parsedEnd: parsedEnd,
    };
  }
  
  const userTimezone = getUserTimeZone();
  const result = {
    parsedStart: start ? utcToZonedTime(new Date(start), userTimezone) : null,
    parsedEnd: end ? utcToZonedTime(new Date(end), userTimezone) : null,
  };
  
  console.log('TIMED Result:', {
    parsedStart: result.parsedStart?.toString(),
    parsedEnd: result.parsedEnd?.toString()
  });
  console.log('=== formatDates END ===');
  
  return result;
}

export function formatWhen({ startDate, endDate, startTime, endTime, allDay, timezone = null }) {
  console.log('=== formatWhen START ===');
  console.log('Input:', { startDate, endDate, startTime, endTime, allDay, timezone });
  
  const formatDateToYYYYMMDD = (date: any): string => {
    if (!date) return '';
    
    console.log(`Formatting date: ${date}, type: ${typeof date}`);
    
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day);
      if (isNaN(dateObj.getTime())) {
        console.error(`Invalid date string: ${date}`);
        return '';
      }
      return date;
    }
    
    try {
      let dateObj: Date;
      
      if (date instanceof Date) {
        dateObj = date;
      } else if (typeof date === 'string') {
        dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
          console.error(`Failed to parse date string: ${date}`);
          return '';
        }
      } else {
        console.error(`Invalid date type: ${typeof date}`);
        return '';
      }
      
      return format(dateObj, 'yyyy-MM-dd');
    } catch (error) {
      console.error(`Error formatting date ${date}:`, error);
      return '';
    }
  };
  
  // FIX: Convert time values to string if they're Date objects
  const formatTimeToString = (time: any): string => {
    if (!time) return '00:00';
    
    console.log(`Formatting time: ${time}, type: ${typeof time}`);
    
    if (time instanceof Date) {
      // Extract HH:mm from Date object
      const hours = time.getHours().toString().padStart(2, '0');
      const minutes = time.getMinutes().toString().padStart(2, '0');
      const result = `${hours}:${minutes}`;
      console.log(`Converted Date to time string: ${result}`);
      return result;
    }
    
    if (typeof time === 'string') {
      // Ensure it's in HH:mm format
      if (/^\d{1,2}:\d{2}$/.test(time)) {
        const [hours, minutes] = time.split(':').map(Number);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
    }
    
    // Default
    return '00:00';
  };
  
  const startDateStr = formatDateToYYYYMMDD(startDate);
  const endDateStr = formatDateToYYYYMMDD(endDate);
  // FIX: Convert times to strings
  const startTimeStr = formatTimeToString(startTime);
  const endTimeStr = formatTimeToString(endTime);
  
  console.log('Formatted dates and times:', { 
    startDateStr, 
    endDateStr, 
    startTimeStr, 
    endTimeStr 
  });
  
  if (!startDateStr) {
    throw new Error('Invalid start date');
  }
  
  if (!allDay) {
    if (!endDateStr) {
      throw new Error('Invalid end date');
    }
    
    const userTimezone = timezone || getUserTimeZone();
    
    console.log('Creating timed event with:', {
      startDate: startDateStr,
      startTime: startTimeStr,
      endDate: endDateStr,
      endTime: endTimeStr,
      timezone: userTimezone
    });
    
    // FIX: Use the string times
    const startDateTime = zonedTimeToUtc(
      new Date(`${startDateStr}T${startTimeStr}`),
      userTimezone
    );
    const endDateTime = zonedTimeToUtc(
      new Date(`${endDateStr}T${endTimeStr}`),
      userTimezone
    );
    
    console.log('Date objects created:', {
      startDateTime: startDateTime.toString(),
      endDateTime: endDateTime.toString(),
      startDateTimeValid: !isNaN(startDateTime.getTime()),
      endDateTimeValid: !isNaN(endDateTime.getTime())
    });
    
    const result = {
      start_time: Math.floor(startDateTime.getTime() / 1000),
      end_time: Math.floor(endDateTime.getTime() / 1000),
    };
    
    console.log('TIMED Result:', result);
    console.log('=== formatWhen END ===');
    
    return result;
  }

  const singleDate = startDateStr === endDateStr || !endDateStr;

  if (!singleDate) {
    // FIXED: Use parseDateString for local date
    const endDateObj = parseDateString(endDateStr);
    console.log('formatWhen - Original end date (LOCAL):', endDateStr, 'as Date:', endDateObj.toString());

    // FIX: Add 1 day to end date for Nylas/iCalendar (uses exclusive end dates)
    // User selects Jan 20-21, we send end_date: Jan 22 so event displays correctly
    endDateObj.setDate(endDateObj.getDate() + 1);
    const adjustedEndDate = format(endDateObj, 'yyyy-MM-dd');

    console.log('formatWhen - Adjusted end date (+1 for exclusive):', adjustedEndDate);
    console.log('formatWhen - New Date object:', endDateObj.toString());

    const result = {
      start_date: startDateStr,
      end_date: adjustedEndDate
    };

    console.log('MULTI-DAY Result:', result);
    console.log('=== formatWhen END ===');

    return result;
  }

  const result = {
    date: startDateStr,
  };
  
  console.log('SINGLE-DAY Result:', result);
  console.log('=== formatWhen END ===');
  
  return result;
}

export function formatWhenDragUpdate({ start, end, allDay }) {
  if (!allDay) {
    const userTimezone = getUserTimeZone();
    const utcStart = zonedTimeToUtc(start, userTimezone);
    const utcEnd = end ? zonedTimeToUtc(end, userTimezone) : addHours(utcStart, 1);
    
    return {
      start_time: Math.floor(utcStart.getTime() / 1000),
      end_time: Math.floor(utcEnd.getTime() / 1000),
    };
  }

  if (!end) {
    return { date: format(start, 'yyyy-MM-dd') };
  }

  const adjustedEnd = new Date(end);
  
  const fixedEnd = subSeconds(adjustedEnd, 1);

  return { 
    start_date: format(start, 'yyyy-MM-dd'), 
    end_date: format(fixedEnd, 'yyyy-MM-dd') 
  };
}

export function ensureEventTimezones(
  events: CalendarEvent[],
  calendarCategories: CalendarCategory[],
  userTimezone: string
): CalendarEvent[] {
  const processedEvents = eventWithCategories(events, calendarCategories);
  
  return processedEvents.map(event => {
    if (event.allDay) {
      const startDate = event.start instanceof Date ? event.start : new Date(event.start);
      const endDate = event.end instanceof Date ? event.end : new Date(event.end);
      
      const originalTimezone = event.metadata?.[METADATA_MAP.original_timezone] || 
                              event.originalTimezone || 
                              event.timezone || 
                              userTimezone;
      
      return {
        ...event,
        start: new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()),
        end: new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()),
        timezone: originalTimezone,
        originalTimezone: originalTimezone,
        displayTimezone: userTimezone
      };
    }
    
    return event;
  });
}

export function formatEventTimeDisplay(
  event: CalendarEvent,
  userTimezone: string
): {
  displayTime: string;
  showOriginalTimezone: boolean;
  originalTimezone: string;
  displayTimezone: string;
} {
  if (event.allDay) {
    return {
      displayTime: 'All day',
      showOriginalTimezone: false,
      originalTimezone: '',
      displayTimezone: userTimezone,
    };
  }

  const showOriginalTimezone = !!event.originalTimezone && 
                               event.originalTimezone !== userTimezone;

  return {
    displayTime: format(event.start, 'h:mm a'),
    showOriginalTimezone,
    originalTimezone: event.originalTimezone || '',
    displayTimezone: userTimezone,
  };
}

export function testDateParsing() {
  console.log('=== TEST DATE PARSING ===');
  
  // Test cases
  const testCases = [
    { input: '2025-12-16', description: 'YYYY-MM-DD string' },
    { input: '2025-12-16T00:00:00Z', description: 'ISO string UTC' },
    { input: '2025-12-16T00:00:00-08:00', description: 'ISO string PST' },
    { input: new Date('2025-12-16'), description: 'Date from YYYY-MM-DD' },
    { input: new Date('2025-12-16T00:00:00Z'), description: 'Date from ISO UTC' },
  ];
  
  testCases.forEach((testCase) => {
    console.log(`\nTest: ${testCase.description}`);
    console.log('Input:', testCase.input);
    
    // Method 1: Current way (problematic)
    const date1 = new Date(testCase.input);
    console.log('new Date():', date1.toString());
    console.log('Date.getDate():', date1.getDate());
    console.log('Date.toISOString():', date1.toISOString());
    
    // Method 2: Correct way for all-day events
    if (typeof testCase.input === 'string') {
      const match = testCase.input.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const [, year, month, day] = match;
        const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        console.log('Local parsing (year, month, day):', localDate.toString());
        console.log('Local Date.getDate():', localDate.getDate());
      }
    }
  });
  
  console.log('=== END TEST ===');
}

// Call this in your component to test
// testDateParsing();